import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const ConvertToCustomerModal = ({ isOpen, onClose, onConvert, prospect }) => {
  const [formData, setFormData] = useState({ companyName: '', contactPerson: '', email: '', phone: '', address: '' });
  const [errors, setErrors] = useState({});
  const [converting, setConverting] = useState(false);

  useEffect(() => {
    if (prospect) {
      const primaryContact = prospect?.prospectContacts?.find(c => c?.isPrimary) || prospect?.prospectContacts?.[0];
      setFormData({
        companyName: prospect?.companyName || '',
        contactPerson: primaryContact ? `${primaryContact?.firstName} ${primaryContact?.lastName}` : '',
        email: primaryContact?.email || '',
        phone: primaryContact?.phone || '',
        address: [prospect?.address, prospect?.city, prospect?.state]?.filter(Boolean)?.join(', ') || ''
      });
    }
    setErrors({});
  }, [prospect, isOpen]);

  const validate = () => {
    const errs = {};
    if (!formData?.companyName?.trim()) errs.companyName = 'Company name is required';
    if (!formData?.contactPerson?.trim()) errs.contactPerson = 'Contact person is required';
    if (!formData?.email?.trim()) errs.email = 'Email is required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/?.test(formData?.email)) errs.email = 'Enter a valid email';
    setErrors(errs);
    return Object.keys(errs)?.length === 0;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!validate()) return;
    setConverting(true);
    try {
      await onConvert(formData);
    } finally {
      setConverting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-card rounded-lg shadow-xl w-full max-w-md mx-4">
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
              <Icon name="UserCheck" size={20} className="text-green-600" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-foreground">Convert to Customer</h2>
              <p className="text-xs text-muted-foreground">Create a customer record from this prospect</p>
            </div>
          </div>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground">
            <Icon name="X" size={20} />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div className="bg-amber-50 border border-amber-200 rounded-md p-3 text-sm text-amber-800">
            <Icon name="Info" size={14} className="inline mr-1" />
            This will create a new customer record and mark this prospect as <strong>Won</strong>.
          </div>
          <Input label="Company Name" required value={formData?.companyName}
            onChange={e => setFormData(p => ({ ...p, companyName: e?.target?.value }))}
            error={errors?.companyName} />
          <Input label="Primary Contact" required value={formData?.contactPerson}
            onChange={e => setFormData(p => ({ ...p, contactPerson: e?.target?.value }))}
            error={errors?.contactPerson} />
          <Input label="Email" type="email" required value={formData?.email}
            onChange={e => setFormData(p => ({ ...p, email: e?.target?.value }))}
            error={errors?.email} />
          <Input label="Phone" value={formData?.phone}
            onChange={e => setFormData(p => ({ ...p, phone: e?.target?.value }))} />
          <Input label="Address" value={formData?.address}
            onChange={e => setFormData(p => ({ ...p, address: e?.target?.value }))} />
          <div className="flex justify-end gap-3 pt-2">
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit" variant="success" loading={converting} iconName="UserCheck" iconPosition="left">
              Convert to Customer
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ConvertToCustomerModal;
